import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { useConnectStatus } from '../hooks/useConnectStatus';
import type { Mode, PerModeStatus } from '../types';

const DISMISS_KEY = 'payments.deauth-banner.dismissed-until';
const DISMISS_HOURS = 24;

interface AffectedMode {
  mode: Mode;
  count: number;
}

function readDismissUntil(): number {
  if (typeof window === 'undefined') return 0;
  const raw = window.localStorage.getItem(DISMISS_KEY);
  if (!raw) return 0;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : 0;
}

function affectedModes(test: PerModeStatus, live: PerModeStatus): AffectedMode[] {
  const out: AffectedMode[] = [];
  if (test.deauthorizedAt && test.restaurantsUsingThisMode.length > 0) {
    out.push({ mode: 'test', count: test.restaurantsUsingThisMode.length });
  }
  if (live.deauthorizedAt && live.restaurantsUsingThisMode.length > 0) {
    out.push({ mode: 'prod', count: live.restaurantsUsingThisMode.length });
  }
  return out;
}

/**
 * Sticky red banner shown at the top of the layout shell whenever an owner's
 * Stripe account is deauthorized AND restaurants are still routing through it.
 * Dismiss persists for 24h via localStorage.
 */
export function DeauthorizedBanner() {
  const { data: status } = useConnectStatus();
  const [dismissedUntil, setDismissedUntil] = useState<number>(() => readDismissUntil());

  useEffect(() => {
    if (dismissedUntil <= Date.now()) return;
    const remaining = dismissedUntil - Date.now();
    const timer = window.setTimeout(() => setDismissedUntil(0), remaining);
    return () => window.clearTimeout(timer);
  }, [dismissedUntil]);

  const affected = useMemo<AffectedMode[]>(() => {
    if (!status) return [];
    return affectedModes(status.test, status.live);
  }, [status]);

  if (affected.length === 0) return null;
  if (dismissedUntil > Date.now()) return null;

  const totalRestaurants = affected.reduce((sum, a) => sum + a.count, 0);
  const modesLabel = affected.map((a) => (a.mode === 'prod' ? 'live' : 'test')).join(' and ');

  const onDismiss = () => {
    const until = Date.now() + DISMISS_HOURS * 60 * 60 * 1000;
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DISMISS_KEY, String(until));
    }
    setDismissedUntil(until);
  };

  return (
    <div
      role="alert"
      className="sticky top-16 z-30 border-b border-destructive/40 bg-destructive/10 backdrop-blur-sm"
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-center sm:gap-4 sm:px-4 lg:px-6">
        <div className="flex items-start gap-2 text-sm text-destructive sm:items-center">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 sm:mt-0" aria-hidden />
          <p className="leading-snug">
            <span className="font-semibold">Stripe is disconnected</span> ({modesLabel} mode)
            {' — '}
            {totalRestaurants} restaurant{totalRestaurants === 1 ? '' : 's'} can no longer take
            online payments. Reconnect to resume accepting orders.
          </p>
        </div>
        <div className="flex items-center gap-1 sm:ml-auto">
          <Button asChild size="sm" variant="destructive" className="h-8">
            <Link to="/app/settings/payments">Reconnect</Link>
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
            aria-label="Dismiss for 24 hours"
            onClick={onDismiss}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
